/**
 * FlashBook reading progress — what a learner has done in a book, kept on the
 * device so a hundred-page book is read across as many sittings as it takes.
 *
 * A FlashBook is not a capsule: a book can run to a hundred pages, and the whole
 * point of one is that it is read over days. Until now a read lived entirely in
 * the player's state, which meant a reload — a phone call, a low battery, a tab
 * closed by mistake — threw away every answer on the way, and the only way back
 * into a long book was to start it again.
 *
 * Three ideas shape how it is kept:
 *
 *  - **Two things, not one.** There is the read *in progress* (`open`) and the
 *    *register* of reads that ended (`sessions`). They answer different
 *    questions: the open read is where the learner is, the register is what they
 *    have done. A read moves from the first to the second when it ends — finished
 *    at the report, or abandoned by starting over.
 *  - **On the device, not the server.** A finished read is already written to the
 *    learner's PDS by `createFlashbookPlay`; this is not a second copy of that.
 *    It is the local half: how far into the book they got, which is a fact about
 *    reading on *this* device, and the thing that has to answer on a cold start
 *    before any network call is made. It is also what lets a learner who is
 *    offline close the app on page 40 and reopen on page 40.
 *  - **The answers, kept verbatim.** A resume carries the block results the
 *    learner produced on the pages they have been through. Keeping only a page
 *    number is tempting and wrong: the progress measure, the answered counts and
 *    the report all read from those results, so a resume that restored a position
 *    but not the answers would claim the learner did nothing on the pages they had
 *    already read.
 *
 * This module is pure: every function here is a function of the stored shape and
 * nothing else. Reading and writing the value is
 * `#/lib/flashbookProgressStore`, and the split is a practical one as well as a
 * tidy one — the layout fixtures build a seeded history, and a module that reached
 * for device storage could not be imported by the browser tests at all.
 */
import {getSlideMaxPoints, NON_SCORED_TYPES} from '#/lib/scoring'
import {type FlashBookPage, type FlashBookPageResult} from '#/types/flashbook'

/**
 * What one page cost a read.
 *
 * The unit is a *page* rather than a question because that is what a learner can
 * act on: they cannot turn back to a wrong answer, but they can turn back to page
 * 12. Which questions were missed is already kept for the read in progress (the
 * report's focus list is built from the open read's own results), and keeping
 * every block result of every read for ever would grow the register by a book's
 * worth of answers per reading.
 */
export type FlashBookPageCost = {
  /**
   * The page's own id.
   *
   * The one name a page keeps across an edit — its title can change, and an index
   * changes the moment a page is inserted — so a page can be recognised across
   * readings, which is what "this page keeps beating you" requires.
   */
  pageId: string
  /** Questions on it that were not right: wrong, half right, or passed over. */
  lost: number
}

/**
 * One read of a book, from the moment it began to the moment it ended.
 *
 * `completed` is the difference the learner cares about: a read that reached the
 * report, versus one they set down. An abandoned read is registered rather than
 * forgotten, because "you started this twice" is true and worth being able to
 * say — but it is counted apart from a read *of the book*.
 */
export type FlashBookReadSession = {
  /** The read's identity: fixed when it begins, so a re-finish corrects it. */
  id: string
  startedAt: string
  endedAt: string
  /** Reached the end of the book, rather than being set down part-way. */
  completed: boolean
  /** Where the read got to, 0-based — the furthest page, not the last one seen. */
  pageReached: number
  /** How long the book was when the read happened, for an honest "N of M". */
  pageCount: number
  /** Exercises answered, points earned, and the points on offer in the book. */
  answered: number
  points: number
  maxPoints: number
  timeMs: number
  /**
   * A *practice* rather than a reading of the book.
   *
   * A learner who takes the report's list of questions worth another look and is
   * asked only those has not read the book again, and the register must not say
   * they did: a practice is counted apart from the reads, and it contributes to
   * no total — not the reads, not the points, and above all not the best score.
   * Its questions are the ones the read already counted, so folding its points
   * into the all-time total would count the same answer twice, and its percentage
   * is over a handful of questions rather than the book, so letting it stand as
   * the best score would report a perfect practice as a perfect read.
   */
  kind?: 'practice'
  /** For a practice: the read whose questions it took up. */
  practiceOf?: string
  /** For a practice: how many of its questions came out right this time. */
  practiceFixed?: number
  /**
   * The AppView's id for the play this read became, stamped on after delivery.
   *
   * A later sync uses it to recognise this read among the learner's plays and
   * not file it a second time (see `stampPlay`). Absent on reads taken before
   * the play outbox existed, and on reads whose play has not landed yet.
   */
  playId?: string
  /**
   * The pages this read was *troubled* by, in book order.
   *
   * The row's score says how the read went as a whole and nothing about *where* it
   * went that way: the same 62% is two different readings depending on whether it
   * came from one page or from forty. A learner on their third read wants the pages
   * that beat them every time — the questions they keep meeting and keep getting
   * wrong — and no per-read total can answer that. So each read keeps the pages that
   * cost it something, and the report adds them up (see
   * `#/lib/flashbookPageTrouble`).
   *
   * Only pages that cost something, and only pages the learner actually *worked* on:
   * a page they never touched cannot have beaten them, so a page with no result in
   * this read is not counted at all — which is deliberately not the report's rule
   * for "worth another look", where an exercise nobody reached is worth naming.
   *
   * Absent on a read registered before any of this existed, and on a practice (whose
   * questions the read it was taken from already counted). Both must read as
   * *unknown* rather than as *nothing went wrong*.
   */
  trouble?: FlashBookPageCost[]
}

/**
 * The read that has not ended.
 *
 * Holds the answers as well as the position (see the module note), plus the time
 * spent so far, so a resumed read carries on counting instead of restarting its
 * clock. Its `id` is what ties a later finish back to this exact read.
 */
export type FlashBookOpenRead = {
  id: string
  startedAt: string
  pageIndex: number
  pageCount: number
  pageResults: FlashBookPageResult[]
  timeMs: number
}

/**
 * All-time counters, kept apart from the session list on purpose.
 *
 * The list is bounded — a learner may read the same book twenty times, and the
 * stored value should not grow without limit — so anything counted by walking the
 * list would quietly reset once the list trims. These are accumulated instead, so
 * "read 6 times" stays true after the sixth read is the only one still listed.
 */
export type FlashBookReadTotals = {
  /** Reads that ended, finished or not. */
  reads: number
  /** …of those, the ones that reached the end of the book. */
  completed: number
  points: number
  /**
   * The best percentage ever earned in this book, kept as the score pair of the
   * single read that earned it.
   *
   * A pair rather than two maxima, and that distinction is the whole reason this
   * field is shaped this way: the highest points and the highest points-on-offer
   * can come from two *different* reads, and dividing one by the other reports a
   * percentage no read ever scored. 90/100 and 95/200 are not 95/200.
   */
  best: {points: number; maxPoints: number} | null
}

export type FlashBookReadProgress = {
  bookUri: string
  /**
   * The title as it was when the learner last read the book.
   *
   * Carried so a book's progress can be named without fetching the book, and
   * refreshed on every write, because a book may be renamed between two readings
   * and the title the learner knows is the one from the read.
   */
  bookTitle: string
  updatedAt: string
  /** Ended reads, newest first. Bounded — see {@link SESSION_LIMIT}. */
  sessions: FlashBookReadSession[]
  totals: FlashBookReadTotals
  open: FlashBookOpenRead | null
}

/** How many ended reads are kept per book, newest first. */
export const SESSION_LIMIT = 20

/**
 * How many books keep progress on this device, most recently read first.
 *
 * A learner reads a handful of books, not a catalogue; this exists so a device
 * that has opened a thousand books does not keep a thousand open reads. Chosen by
 * *when a book was last read* rather than when it was first opened, so the one
 * that disappears is the one untouched longest.
 */
export const BOOK_LIMIT = 40

/**
 * How many troubled pages one read keeps.
 *
 * A book of a hundred pages read badly could have a hundred of them, and every one
 * would be a line in a stored record written on every page turn. The cut is by
 * *cost*, so what survives is the worst of the read — and it is the tail that is
 * dropped, which is what makes the report's counts a floor rather than a guess: a
 * page can only be under-counted, never invented.
 */
export const TROUBLE_LIMIT = 40

/**
 * How many missed questions one finished read stores.
 *
 * A book of a hundred questions answered badly could list a hundred of them, and
 * every one would be a string in a stored session. The cut keeps the front of the
 * list — the start of the book rather than a random sample — and `missedCount`
 * records that a cut happened so the difference below is not read as a fact when
 * it is only a floor.
 */
export const MISSED_LIMIT = 40

/**
 * The pages a read was troubled by, from the book and the read's own results.
 *
 * "Troubled" means the learner was *on* the page — the player records a page only
 * when something on it reports an answer — and something on it was not right. A
 * question left untouched on a page the learner worked on counts as lost, which is
 * the same rule the report's focus list uses: a question that was passed over is a
 * question the book still has for them.
 *
 * Deliberately the same `NON_SCORED_TYPES` the report's `isScoredBlock` asks about,
 * so "a question" means one thing in both places.
 */
export function pageCosts(
  pages: readonly FlashBookPage[],
  pageResults: readonly FlashBookPageResult[],
): FlashBookPageCost[] {
  const costs: (FlashBookPageCost & {index: number})[] = []
  pages.forEach((page, index) => {
    const result = pageResults.find(r => r.pageId === page.id)
    // Never opened: a page with nothing reported on it cannot have cost anything,
    // and counting it would report every page after the one a learner stopped on.
    if (!result) return
    let lost = 0
    for (const block of page.blocks) {
      if (NON_SCORED_TYPES.has(block.type)) continue
      const blockResult = result.blocks.find(b => b.blockId === block.id)
      if (blockResult?.isCorrect === true) continue
      lost += 1
    }
    if (lost > 0) costs.push({pageId: page.id, lost, index})
  })

  return (
    costs
      // The worst first, so the limit keeps the read's real trouble rather than
      // whichever pages happen to be early in the book…
      .sort((a, b) => b.lost - a.lost || a.index - b.index)
      .slice(0, TROUBLE_LIMIT)
      // …and then back into book order, which is the order they are read in and so
      // the order they are worth looking at.
      .sort((a, b) => a.index - b.index)
      .map(({pageId, lost}) => ({pageId, lost}))
  )
}

/**
 * The questions a set of results was not right on, in book order.
 *
 * "Not right" means left wrong, half right, passed over on a page the learner
 * worked on, or on a page they never turned to. A question is an exercise block
 * that is not in `NON_SCORED_TYPES`.
 */
export function missedQuestions(
  pages: readonly FlashBookPage[],
  pageResults: readonly FlashBookPageResult[],
): string[] {
  const missed: string[] = []
  for (const page of pages) {
    const result = pageResults.find(r => r.pageId === page.id)
    for (const block of page.blocks) {
      if (NON_SCORED_TYPES.has(block.type)) continue
      const blockResult = result?.blocks.find(b => b.blockId === block.id)
      if (blockResult?.isCorrect === true) continue
      missed.push(block.id)
    }
  }
  return missed
}

/**
 * What a read stores about the questions it missed: the list, cut, and its true
 * length.
 *
 * One function rather than two fields set by hand at each of the places a read
 * is registered, because the two have to agree for `MISSED_LIMIT`'s reasoning
 * to hold — a list cut without the count is a list that reads as complete.
 */
export function missedDigest(
  pages: readonly FlashBookPage[],
  pageResults: readonly FlashBookPageResult[],
): {missed: string[]; missedCount: number} {
  const all = missedQuestions(pages, pageResults)
  return {missed: all.slice(0, MISSED_LIMIT), missedCount: all.length}
}

export function emptyProgress(
  bookUri: string,
  bookTitle: string,
  now: string = new Date().toISOString(),
): FlashBookReadProgress {
  return {
    bookUri,
    bookTitle,
    updatedAt: now,
    sessions: [],
    totals: {reads: 0, completed: 0, points: 0, best: null},
    open: null,
  }
}

/**
 * An identifier for a read, unique without a random source.
 *
 * A timestamp is the basis — it sorts, which is what lets the session list's
 * ordering fall out of the data rather than a second sort key — but a timestamp
 * alone is not an identity: a learner who presses "start over" twice inside one
 * millisecond, or begins and finishes in the same one, would give two *different*
 * reads the same id, and the second would then be taken for a correction of the
 * first instead of a read of its own. So a taken id is suffixed rather than
 * reused.
 */
function readId(startedAt: string, taken: (id: string) => boolean): string {
  const base = `read-${startedAt}`
  if (!taken(base)) return base
  let n = 2
  while (taken(`${base}-${n}`)) n += 1
  return `${base}-${n}`
}

/** Exercises answered in a set of page results, and the points they earned. */
export function resultsSummary(pageResults: readonly FlashBookPageResult[]): {
  answered: number
  points: number
} {
  let answered = 0
  let points = 0
  for (const page of pageResults) {
    for (const block of page.blocks) {
      answered += 1
      points += block.points ?? 0
    }
  }
  return {answered, points}
}

/**
 * The points on offer in the book, or up to a page.
 *
 * Read with the same two pieces of shared scoring the report uses — which types
 * are gradeable at all, and what each one is worth — so a session's percentage can
 * never disagree with the report's. That is the one way this feature could quietly
 * lie about how a read went, and it is worth naming the exact trap: a `content`
 * block is not an exercise, but asking `getSlideMaxPoints` about one answers `1`,
 * because it is the default for any type it does not know. Skipping the
 * non-scored types is what keeps a book of pure reading from claiming points on
 * offer that nobody could earn.
 */
export function pointsOnOffer(
  pages: readonly FlashBookPage[],
  upToPage: number = pages.length - 1,
): number {
  let max = 0
  pages.forEach((page, index) => {
    if (index > upToPage) return
    for (const block of page.blocks) {
      if (NON_SCORED_TYPES.has(block.type)) continue
      max += getSlideMaxPoints(block)
    }
  })
  return max
}

/**
 * Begin a fresh read.
 *
 * An open read set aside here is *registered*, not discarded: the learner chose
 * to start over rather than continue, and the read they abandoned is part of what
 * they have done with the book. It is registered as unfinished, which is exactly
 * what it was.
 */
export function beginRead(
  progress: FlashBookReadProgress,
  {
    pages,
    now = new Date().toISOString(),
  }: {pages: readonly FlashBookPage[]; now?: string},
): FlashBookReadProgress {
  let base = progress
  const open = progress.open
  if (open) {
    const summary = resultsSummary(open.pageResults)
    base = registerSession(base, {
      id: open.id,
      startedAt: open.startedAt,
      endedAt: now,
      completed: false,
      pageReached: Math.min(open.pageIndex, Math.max(0, open.pageCount - 1)),
      pageCount: pages.length || open.pageCount,
      answered: summary.answered,
      points: summary.points,
      maxPoints: pointsOnOffer(pages),
      timeMs: open.timeMs,
      // The read being set aside is registered like any other, so it says where it
      // struggled too: a learner who starts over on page 40 has told us something
      // about the pages they *did* read, and only the score of the abandon is kept
      // otherwise.
      trouble: pageCosts(pages, open.pageResults),
    })
  }

  return {
    ...base,
    updatedAt: now,
    open: {
      id: readId(now, id => base.sessions.some(s => s.id === id)),
      startedAt: now,
      pageIndex: 0,
      pageCount: pages.length,
      pageResults: [],
      timeMs: 0,
    },
  }
}

/**
 * Adopt the open read as the one in progress, against the book as it is now.
 *
 * Called when a learner continues, because a book can be edited between two
 * sittings: the page they stopped on may no longer exist, and neither may the
 * ones after it. Only the position and the page count are reconciled; the answers
 * are restored exactly as they were given.
 */
export function resumeRead(
  progress: FlashBookReadProgress,
  {
    pages,
    now = new Date().toISOString(),
  }: {pages: readonly FlashBookPage[]; now?: string},
): FlashBookReadProgress {
  const open = progress.open
  if (!open) return progress
  return {
    ...progress,
    updatedAt: now,
    open: {
      ...open,
      pageIndex: clampPage(open.pageIndex, pages.length),
      pageCount: pages.length,
    },
  }
}

/** Where to put a resume, or `null` when there is nothing to continue. */
export type FlashBookResumeTarget = {
  sessionId: string
  startedAt: string
  pageIndex: number
  pageCount: number
  pageResults: FlashBookPageResult[]
  timeMs: number
}

/**
 * What a resume restores, or `null`.
 *
 * A read that began and never left its first page is deliberately *not* a resume:
 * there is nothing to continue, and offering "continue from page 1" next to
 * "start reading" is two buttons that do the same thing — which is how a learner
 * learns to stop reading either of them.
 */
export function resumeTarget(
  progress: FlashBookReadProgress | null | undefined,
): FlashBookResumeTarget | null {
  const open = progress?.open
  if (!open) return null
  const touched = resultsSummary(open.pageResults).answered > 0
  if (open.pageIndex <= 0 && !touched) return null
  return {
    sessionId: open.id,
    startedAt: open.startedAt,
    pageIndex: open.pageIndex,
    pageCount: open.pageCount,
    pageResults: open.pageResults,
    timeMs: open.timeMs,
  }
}

/**
 * Save where the read is, as it goes.
 *
 * A no-op when no read is open, and that is what makes it safe to call from a
 * render-time effect and from an unmount flush alike: a player that is only
 * *previewing* a book never opens a read, and a write that arrives late must not
 * create one. The absent state is left absent rather than being recreated, so a
 * save can never resurrect a read the learner has finished.
 */
export function saveReadPosition(
  progress: FlashBookReadProgress,
  {
    sessionId,
    pageIndex,
    pageResults,
    timeMs,
    bookTitle,
    now = new Date().toISOString(),
  }: {
    sessionId: string
    pageIndex: number
    pageResults: FlashBookPageResult[]
    timeMs: number
    bookTitle?: string
    now?: string
  },
): FlashBookReadProgress {
  const open = progress.open
  if (!open || open.id !== sessionId) return progress
  return {
    ...progress,
    bookTitle: bookTitle ?? progress.bookTitle,
    updatedAt: now,
    open: {
      ...open,
      pageIndex: clampPage(pageIndex, safePageCount(open.pageCount)),
      pageResults,
      timeMs,
    },
  }
}

/**
 * End a read, and put it in the register.
 *
 * Written as an *upsert* keyed by the read's id, and that is load-bearing: a
 * learner who reaches the report, goes back into the book from a question the
 * report named, and finishes again is still reading the same book — so the second
 * finish must correct the first registration rather than record a second reading
 * of a book that was read once. It is also what makes a double finish harmless.
 *
 * `maxPoints` is the points on offer in the whole book while the score is what was
 * earned, so a read abandoned on page 4 of 60 reports a low percentage, and is
 * meant to: it is the honest answer to "how did the read go".
 */
export function finishRead(
  progress: FlashBookReadProgress,
  {
    sessionId,
    startedAt,
    pages,
    pageResults,
    timeMs,
    pageReached,
    completed = true,
    bookTitle,
    now = new Date().toISOString(),
  }: {
    sessionId: string
    startedAt?: string
    pages: readonly FlashBookPage[]
    pageResults: FlashBookPageResult[]
    timeMs: number
    pageReached?: number
    completed?: boolean
    bookTitle?: string
    now?: string
  },
): FlashBookReadProgress {
  const open = progress.open
  const beganAt = startedAt ?? open?.startedAt ?? now
  const summary = resultsSummary(pageResults)
  const session: FlashBookReadSession = {
    id: sessionId,
    startedAt: beganAt,
    endedAt: now,
    completed,
    pageReached: clampPage(pageReached ?? open?.pageIndex ?? 0, pages.length),
    pageCount: pages.length,
    answered: summary.answered,
    points: summary.points,
    maxPoints: pointsOnOffer(pages),
    timeMs,
    // Where this read went wrong, page by page — the half of it a score cannot
    // say, and the half a learner on their third read is asking about.
    trouble: pageCosts(pages, pageResults),
  }
  const withSession = registerSession(progress, session)
  return {
    ...withSession,
    bookTitle: bookTitle ?? withSession.bookTitle,
    updatedAt: now,
    // Only this read is closed; another open read would be somebody else's to
    // finish.
    open: open?.id === sessionId ? null : withSession.open,
  }
}

/**
 * Register a practice — the questions a report named, sat once more.
 *
 * It does not open a read and it does not close one: a practice is a thing done
 * *around* a read, and the read it belongs to is already in the register. So this
 * writes a session straight into the list, tagged as a practice, and leaves the
 * open read exactly as it found it — which is what stops a learner practising a
 * finished book from having their read reopened underneath them.
 *
 * `maxPoints` is the points on offer in the *practice*, so its percentage is the
 * honest answer to "how did the practice go" and is comparable with the report's
 * own score for the same questions (see `#/lib/flashbookReRead`).
 */
export function recordPractice(
  progress: FlashBookReadProgress,
  {
    startedAt,
    pages,
    results,
    ofSessionId,
    fixed,
    timeMs,
    bookTitle,
    now = new Date().toISOString(),
  }: {
    /** When the practice began, so its duration is its own and not a guess. */
    startedAt: string
    /** The practice's own pages — the book cut down to these questions. */
    pages: readonly FlashBookPage[]
    results: readonly FlashBookPageResult[]
    /** The read whose questions these were. */
    ofSessionId: string
    /** How many of them came out right this time. */
    fixed: number
    timeMs: number
    bookTitle?: string
    now?: string
  },
): FlashBookReadProgress {
  const summary = resultsSummary(results)
  const session: FlashBookReadSession = {
    id: readId(now, id => progress.sessions.some(s => s.id === id)),
    startedAt,
    endedAt: now,
    completed: true,
    pageReached: Math.max(0, pages.length - 1),
    pageCount: pages.length,
    answered: summary.answered,
    points: summary.points,
    maxPoints: pointsOnOffer(pages),
    timeMs,
    kind: 'practice',
    practiceOf: ofSessionId,
    practiceFixed: fixed,
  }
  return {
    ...registerSession(progress, session),
    bookTitle: bookTitle ?? progress.bookTitle,
    updatedAt: now,
  }
}

/**
 * File a read that was taken on another device (or another platform) into this
 * device's register.
 *
 * `updatedAt` moves to *now*, not to the read's own end: it is when this device
 * touched the book, which is what the store trims by and what a shelf sorts on.
 */
export function adoptRead(
  progress: FlashBookReadProgress,
  session: FlashBookReadSession,
  {now = new Date().toISOString()}: {now?: string} = {},
): FlashBookReadProgress {
  return {...registerSession(progress, session), updatedAt: now}
}

/**
 * Record which play a read became, once the server has named it.
 *
 * The play's id is what a later sync uses to recognise this read among the
 * learner's plays, so the device that took the read never files it a second time
 * (see `adoptRead`). It arrives after the read is already registered — the play is
 * posted and answered while the report is drawing — so it is stamped on afterwards
 * rather than passed in at the finish.
 */
export function stampPlay(
  progress: FlashBookReadProgress,
  {sessionId, playId}: {sessionId: string; playId: string},
): FlashBookReadProgress {
  if (!playId) return progress
  if (!progress.sessions.some(session => session.id === sessionId))
    return progress
  return {
    ...progress,
    sessions: progress.sessions.map(session =>
      session.id === sessionId ? {...session, playId} : session,
    ),
  }
}

/**
 * Put a session in the register, newest first, and keep the counters.
 *
 * Counters move *by delta*, never by re-adding: a corrected re-finish replaces a
 * session it has already counted, so adding the new score again would report a
 * book read once as having earned twice. `reads` moves only on first
 * registration, so "read 3 times" is a count of reads and not of finishes. The
 * best score is decided by *percentage* and stored as the pair that earned it,
 * because the highest points and the highest points-on-offer can come from
 * different reads (see {@link FlashBookReadTotals}).
 */
function registerSession(
  progress: FlashBookReadProgress,
  session: FlashBookReadSession,
): FlashBookReadProgress {
  const existing = progress.sessions.find(s => s.id === session.id)
  const sessions = [
    session,
    ...progress.sessions.filter(s => s.id !== session.id),
  ]
    .sort((a, b) => Date.parse(b.endedAt) - Date.parse(a.endedAt))
    .slice(0, SESSION_LIMIT)

  /*
   * A practice is *listed* and *counted nothing*. It is in the register because
   * the learner did it and it is part of what they have done with this book, and
   * it moves no total because every number a total carries is about the book —
   * and a practice is about eight questions of it (see `kind`).
   */
  if (session.kind === 'practice') {
    return {...progress, sessions}
  }

  const totals: FlashBookReadTotals = {...progress.totals}
  if (existing) {
    totals.points += session.points - existing.points
    if (existing.completed !== session.completed) {
      totals.completed += session.completed ? 1 : -1
    }
  } else {
    totals.reads += 1
    totals.completed += session.completed ? 1 : 0
    totals.points += session.points
  }

  const storedPct = percentageOf(totals.best) ?? -1
  const sessionPct = percentageOf(session) ?? -1
  if (sessionPct > storedPct) {
    totals.best = {points: session.points, maxPoints: session.maxPoints}
  }

  return {...progress, sessions, totals}
}

function percentageOf(
  score: {points: number; maxPoints: number} | null | undefined,
): number | null {
  if (!score || score.maxPoints <= 0) return null
  return Math.round((score.points / score.maxPoints) * 100)
}

/**
 * What a cover can say about a book without opening it.
 *
 * Percentages are `null` rather than 0 when nothing has been scored, so the UI
 * never prints "best 0%" for a book that has only ever been read — or for a book
 * whose exercises earn nothing — which would be a claim about the learner rather
 * than about the data.
 */
export type FlashBookReadSummary = {
  /** Reads that ended, finished or not. */
  reads: number
  /** …of those, the ones that reached the end of the book. */
  completed: number
  /** The best percentage earned, or `null` when nothing has been scored. */
  bestPct: number | null
  /** When the book was last read — the last end, or the open read's start. */
  lastReadAt: string | null
  /** The page a resume would land on, 1-based, or `null` when there is none. */
  resumePage: number | null
  /** How long the book was at that read, for "page 12 of 40". */
  resumeOf: number | null
  /** Points earned across every read, all-time. */
  points: number
  /**
   * Practices sat on this book, all-time.
   *
   * Read off the listed sessions rather than every practice ever sat, and that is
   * the honest limit of it: the list is bounded, so a learner who practises fifty
   * times is told about the ones still listed. It is a count of a *list*, kept that
   * way deliberately — a practice carries no total of its own to accumulate, and
   * the zero it contributes to every other number here is the point.
   */
  practices: number
}

export function summariseProgress(
  progress: FlashBookReadProgress | null | undefined,
): FlashBookReadSummary | null {
  if (!progress) return null
  const resume = resumeTarget(progress)
  if (progress.totals.reads === 0 && !resume) return null
  return {
    reads: progress.totals.reads,
    completed: progress.totals.completed,
    bestPct: percentageOf(progress.totals.best),
    lastReadAt:
      progress.sessions.find(s => s.kind !== 'practice')?.endedAt ??
      progress.open?.startedAt ??
      null,
    resumePage: resume ? resume.pageIndex + 1 : null,
    resumeOf: resume ? resume.pageCount : null,
    points: progress.totals.points,
    practices: progress.sessions.filter(s => s.kind === 'practice').length,
  }
}

/**
 * How far through its exercises an open read is, as a percentage.
 *
 * Measured in *answered exercises*, not pages turned: a learner who reached page
 * 60 of 100 by flicking through has not read 60% of the book, and the running
 * progress measure on the page agrees — this is that same measure.
 */
export function openReadPct(
  progress: FlashBookReadProgress | null | undefined,
  pages: readonly FlashBookPage[],
): number {
  const open = progress?.open
  if (!open) return 0
  const total = pages.reduce(
    (a, page) =>
      a + page.blocks.filter(b => !NON_SCORED_TYPES.has(b.type)).length,
    0,
  )
  if (total <= 0) return 0
  return Math.min(
    100,
    (resultsSummary(open.pageResults).answered / total) * 100,
  )
}

function clampPage(index: number, pageCount: number): number {
  if (!Number.isFinite(index)) return 0
  return Math.max(0, Math.min(Math.max(0, pageCount - 1), Math.trunc(index)))
}

/**
 * A page count, guarded against a book with no pages at all.
 *
 * The open read's own count is what a save is clamped against, and a book that has
 * been emptied since the read would otherwise clamp every position to -1 and lose
 * the place entirely.
 */
function safePageCount(pageCount: number): number {
  return Number.isFinite(pageCount) ? Math.max(1, Math.trunc(pageCount)) : 1
}
